/**
 * Página de Redefinição de Senha
 *
 * Tela para criar nova senha após clicar no link do email.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PASSWORD_HINT, strongPasswordField } from '@utils/passwordPolicy';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { Spinner } from '@components/ui/Spinner';
import { api } from '@services/api';
import toast from 'react-hot-toast';
import { logoSquareWhite, logoSquarePrimary } from '@assets/index';

/**
 * Schema de validação
 */
const resetPasswordSchema = z
  .object({
    // Mesmas regras do cadastro e do servidor (#289)
    password: strongPasswordField('Senha é obrigatória'),
    confirmPassword: z.string().min(1, 'Confirmação é obrigatória'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'As senhas não conferem',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

/**
 * Componente de página de Redefinição de Senha
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Estados
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isInvalidToken, setIsInvalidToken] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Configuração do formulário
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  /**
   * Valida o token ao carregar a página
   */
  useEffect(() => {
    async function validateToken() {
      if (!token) {
        setIsInvalidToken(true);
        setIsValidating(false);
        return;
      }

      try {
        await api.get(`/auth/reset-password/${token}`);
        setIsValidating(false);
      } catch {
        setIsInvalidToken(true);
        setIsValidating(false);
      }
    }

    validateToken();
  }, [token]);

  /**
   * Handler de submit do formulário
   */
  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    setIsLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token,
        password: data.password,
        confirmPassword: data.confirmPassword,
      });

      setIsSuccess(true);
      toast.success('Senha alterada com sucesso!');

      // Redireciona para login após 3 segundos
      setTimeout(() => {
        navigate('/entrar');
      }, 3000);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Erro ao redefinir senha');
    } finally {
      setIsLoading(false);
    }
  };

  // Tela de loading (validando token)
  if (isValidating) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Validando link de recuperação...
          </p>
        </div>
      </div>
    );
  }

  // Tela de token inválido
  if (isInvalidToken) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-error-100 dark:bg-error-900/30">
            <AlertCircle size={40} className="text-error-600" />
          </div>

          <h1 className="text-2xl font-bold text-primary-900 dark:text-white">
            Link inválido ou expirado
          </h1>

          <p className="mt-4 text-gray-600 dark:text-gray-400">
            O link de recuperação de senha é inválido ou já expirou.
            Solicite um novo link para redefinir sua senha.
          </p>

          <Link to="/esqueci-senha" className="btn btn-primary mt-8 inline-flex">
            Solicitar novo link
          </Link>
        </div>
      </div>
    );
  }

  // Tela de sucesso
  if (isSuccess) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success-100 dark:bg-success-900/30">
            <CheckCircle size={40} className="text-success-700" />
          </div>

          <h1 className="text-2xl font-bold text-primary-900 dark:text-white">
            Senha alterada!
          </h1>

          <p className="mt-4 text-gray-600 dark:text-gray-400">
            Sua senha foi alterada com sucesso.
            Você será redirecionado para a página de login.
          </p>

          <Link to="/entrar" className="btn btn-primary mt-8 inline-flex">
            Ir para login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img
            src={logoSquarePrimary}
            alt="VendeAi"
            className="h-20 w-20 rounded-2xl object-contain shadow-lg dark:hidden"
          />
          <img
            src={logoSquareWhite}
            alt="VendeAi"
            className="hidden h-20 w-20 rounded-2xl object-contain shadow-lg dark:block"
          />
        </div>

        {/* Título */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-primary-900 dark:text-white">
            Criar nova senha
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Digite sua nova senha abaixo.
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Campo Senha */}
          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Nova senha
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('password')}
                className={errors.password ? 'w-full border-error-500 pr-10' : 'w-full pr-10'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <span className="form-error">{errors.password.message}</span>
            )}
            <span className="form-hint">{PASSWORD_HINT}</span>
          </div>

          {/* Campo Confirmar Senha */}
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirmar nova senha
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('confirmPassword')}
                className={
                  errors.confirmPassword ? 'w-full border-error-500 pr-10' : 'w-full pr-10'
                }
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="form-error">{errors.confirmPassword.message}</span>
            )}
          </div>

          {/* Botão de submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? (
              <Spinner size="sm" color="white" />
            ) : (
              <>
                <Lock size={20} />
                Redefinir senha
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
